import React, {useRef} from 'react'
import styled from '@emotion/styled'
import {SelectedElement} from 'utils/selectedElement'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {OfferLayout} from '@vio/shared/src/search/OfferLayout'

const SeeAllWrapper = styled(Text)`
  display: flex;
  align-items: center;
`

const Wrapper = styled.div`
  cursor: pointer;
  ${linariaMq.desktopXs} {
    .--more-deals {
      line-height: 20px !important;
    }
  }
`

// Anchor variant: same styling, but a real link so cmd/middle-click open the
// AP in a new tab
const LinkWrapper = styled(NoStyleLink)`
  cursor: pointer;
  ${linariaMq.desktopXs} {
    .--more-deals {
      line-height: 20px !important;
    }
  }
`

interface Props {
  onClick: () => void
  href?: string
}

export const MoreDealsButtonVertical: React.FC<Props> = ({onClick, href}) => {
  const {isDesktop} = useDeviceLayout()
  const containerRef = useRef<HTMLElement | null>(null)

  if (isDesktop) {
    const content = (
      <OfferLayout
        // dataId handles click on HotelOverlay and opens Deals Tab
        dataId={SelectedElement.ShowAllDeals}
        variant="SecondaryInline"
        priceComponent={
          <SeeAllWrapper variant="labelS">
            <Box
              data-id="ArrowIcon"
              hasMirror
              marginLeft="s200"
              color="content.neutral.secondary"
            >
              <Icon name="ChevronRight" size="sm" />
            </Box>
          </SeeAllWrapper>
        }
        providerLogoComponent={
          <Text variant="labelXS" className="--more-deals">
            <FormattedMessageWrapper
              id="hotelCard.moreDeals"
              defaultMessage="More deals"
            />
          </Text>
        }
      />
    )

    return (
      <>
        {/* An href renders a real anchor so cmd/middle-click work */}
        {href ? (
          <LinkWrapper
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            ref={containerRef as React.RefObject<HTMLAnchorElement>}
          >
            {content}
          </LinkWrapper>
        ) : (
          <Wrapper
            onClick={onClick}
            ref={containerRef as React.RefObject<HTMLDivElement>}
          >
            {content}
          </Wrapper>
        )}
      </>
    )
  }

  return (
    <Wrapper onClick={onClick}>
      <OfferLayout
        variant="SecondaryDefault"
        priceComponent={
          <SeeAllWrapper variant="labelS">
            <FormattedMessageWrapper
              id="hotelCard.seeAll"
              defaultMessage="See all"
            />
            <Box
              data-id="ArrowIcon"
              hasMirror
              marginLeft="s200"
              color="content.neutral.secondary"
            >
              <Icon name="ChevronRight" size="sm" />
            </Box>
          </SeeAllWrapper>
        }
        providerLogoComponent={
          <Text variant="labelS" className="--more-deals">
            <FormattedMessageWrapper
              id="hotelCard.moreDeals"
              defaultMessage="More deals"
            />
          </Text>
        }
      />
    </Wrapper>
  )
}
